// src/pages/EditProductPage.tsx - VERSION COMPLÈTE avec delivery_fee
import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import productService, { Product, ProductUpdate } from '../services/productService';

interface Category {
  id: number;
  name: string;
}

const EditProductPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [originalProduct, setOriginalProduct] = useState<Product | null>(null);
  
  // ✅ MODIFIÉ - Ajouter delivery_fee dans formData
  const [formData, setFormData] = useState<ProductUpdate>({
    name: '',
    description: '',
    price: 0,
    category_id: 0,
    stock: 0,
    delivery_fee: 0  // ✅ NOUVEAU CHAMP
  });
  
  const [newImage, setNewImage] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);
  const [success, setSuccess] = useState('');
  const [categories, setCategories] = useState<Category[]>([]);
  const [hasChanges, setHasChanges] = useState(false);

  // Vérifier que l'utilisateur est un farmer
  useEffect(() => {
    if (user && !user.is_farmer) {
      navigate('/');
    }
  }, [user, navigate]);

  // Charger les catégories
  useEffect(() => {
    setCategories([
      { id: 1, name: 'Céréales' },
      { id: 2, name: 'Légumes' },
      { id: 3, name: 'Fruits' },
      { id: 4, name: 'Légumineuses' },
      { id: 5, name: 'Tubercules' },
      { id: 6, name: 'Épices et aromates' }
    ]);
  }, []);

  // Charger le produit à modifier
  useEffect(() => {
    if (id && user?.is_farmer) {
      loadProduct();
    }
  }, [id, user]);

  const loadProduct = async () => {
    try {
      setLoading(true);
      const product = await productService.getProduct(parseInt(id!));
      
      // Vérifier que l'utilisateur est propriétaire du produit
      if (product.owner_id !== user?.id && !user?.is_admin) {
        alert('Vous n\'avez pas l\'autorisation de modifier ce produit');
        navigate('/my-products');
        return;
      }
      
      setOriginalProduct(product);
      
      // ✅ MODIFIÉ - Inclure delivery_fee dans l'initialisation
      setFormData({
        name: product.name,
        description: product.description,
        price: product.price,
        category_id: product.category_id,
        stock: product.stock,
        delivery_fee: product.delivery_fee || 0  // ✅ NOUVEAU
      });
      setPreview(productService.getImageUrl(product.image_url));
    } catch (err: any) {
      console.error('Erreur lors du chargement du produit:', err);
      if (err.response?.status === 404) {
        alert('Produit non trouvé');
      } else if (err.response?.status === 403) {
        alert('Accès non autorisé');
      } else {
        alert('Erreur lors du chargement du produit');
      }
      navigate('/my-products');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    
    // ✅ MODIFIÉ - Inclure delivery_fee dans les champs numériques
    const numericValue = (name === 'price' || name === 'stock' || name === 'category_id' || name === 'delivery_fee') 
      ? parseFloat(value) || 0 
      : value;
    
    setFormData(prev => ({
      ...prev,
      [name]: numericValue
    }));
    
    // ✅ MODIFIÉ - Vérifier s'il y a des changements avec delivery_fee
    if (originalProduct) {
      const hasChanges = 
        (name === 'name' && value !== originalProduct.name) ||
        (name === 'description' && value !== originalProduct.description) ||
        (name === 'price' && parseFloat(value) !== originalProduct.price) ||
        (name === 'category_id' && parseInt(value) !== originalProduct.category_id) ||
        (name === 'stock' && parseInt(value) !== originalProduct.stock) ||
        (name === 'delivery_fee' && parseFloat(value) !== (originalProduct.delivery_fee || 0)) || // ✅ NOUVEAU
        newImage !== null;
      
      setHasChanges(hasChanges);
    }
    
    // Effacer les erreurs
    if (errors.length > 0) {
      setErrors([]);
    }
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Valider l'image
      const imageErrors = productService.validateImageFile(file);
      if (imageErrors.length > 0) {
        setErrors(imageErrors);
        return;
      }
      
      setNewImage(file);
      setHasChanges(true);
      setErrors([]);
      
      // Créer un aperçu de la nouvelle image
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors([]);
    setSuccess('');
    setSaving(true);

    try {
      // Validation des données
      const validationErrors = productService.validateProductData({
        name: formData.name || '',
        description: formData.description || '',
        price: formData.price || 0,
        category_id: formData.category_id || 0,
        stock: formData.stock || 0,
        delivery_fee: formData.delivery_fee || 0  // ✅ NOUVEAU
      });
      
      if (validationErrors.length > 0) {
        setErrors(validationErrors);
        setSaving(false);
        return;
      }

      // ✅ MODIFIÉ - Préparer les données modifiées avec delivery_fee
      const updateData: ProductUpdate = {};
      
      if (formData.name !== originalProduct?.name) {
        updateData.name = formData.name;
      }
      if (formData.description !== originalProduct?.description) {
        updateData.description = formData.description;
      }
      if (formData.price !== originalProduct?.price) {
        updateData.price = formData.price;
      }
      if (formData.category_id !== originalProduct?.category_id) {
        updateData.category_id = formData.category_id;
      }
      if (formData.stock !== originalProduct?.stock) {
        updateData.stock = formData.stock;
      }
      // ✅ NOUVEAU - Vérification delivery_fee
      if (formData.delivery_fee !== (originalProduct?.delivery_fee || 0)) {
        updateData.delivery_fee = formData.delivery_fee;
      }

      // Mettre à jour le produit
      const updatedProduct = await productService.updateProduct(
        parseInt(id!), 
        updateData, 
        newImage || undefined
      );
      
      setSuccess('Produit mis à jour avec succès !');
      setOriginalProduct(updatedProduct);
      setNewImage(null);
      setHasChanges(false);
      
      // Rediriger après 2 secondes
      setTimeout(() => {
        navigate('/my-products');
      }, 2000);
      
    } catch (err: any) {
      console.error('Erreur lors de la mise à jour:', err);
      
      if (err.response?.data?.detail) {
        setErrors([err.response.data.detail]);
      } else if (err.response?.status === 403) {
        setErrors(['Vous n\'avez pas l\'autorisation de modifier ce produit']);
      } else {
        setErrors(['Erreur lors de la mise à jour du produit. Veuillez réessayer.']);
      }
    } finally {
      setSaving(false);
    }
  };

  const resetForm = () => {
    if (originalProduct) {
      setFormData({
        name: originalProduct.name,
        description: originalProduct.description,
        price: originalProduct.price,
        category_id: originalProduct.category_id,
        stock: originalProduct.stock,
        delivery_fee: originalProduct.delivery_fee || 0  // ✅ NOUVEAU
      });
      setNewImage(null);
      setPreview(productService.getImageUrl(originalProduct.image_url));
      setHasChanges(false);
      setErrors([]);
    }
  };

  const handleCancel = () => {
    if (hasChanges) {
      if (window.confirm('Vous avez des modifications non sauvegardées. Voulez-vous vraiment quitter ?')) {
        navigate('/my-products');
      }
    } else {
      navigate('/my-products');
    }
  };

  // ✅ MODIFIÉ - Calculer les changements avec delivery_fee
  const getChanges = () => {
    if (!originalProduct) return [];
    
    const changes = [];
    if (formData.name !== originalProduct.name) {
      changes.push(`Nom: "${originalProduct.name}" → "${formData.name}"`);
    }
    if (formData.description !== originalProduct.description) {
      changes.push(`Description modifiée`);
    }
    if (formData.price !== originalProduct.price) {
      changes.push(`Prix: ${originalProduct.price} → ${formData.price} FCFA`);
    }
    if (formData.category_id !== originalProduct.category_id) {
      const oldCat = categories.find(c => c.id === originalProduct.category_id)?.name || 'Inconnue';
      const newCat = categories.find(c => c.id === formData.category_id)?.name || 'Inconnue';
      changes.push(`Catégorie: ${oldCat} → ${newCat}`);
    }
    if (formData.stock !== originalProduct.stock) {
      changes.push(`Stock: ${originalProduct.stock} → ${formData.stock} unités`);
    }
    // ✅ NOUVEAU - Changement delivery_fee
    if (formData.delivery_fee !== (originalProduct.delivery_fee || 0)) {
      const oldFee = originalProduct.delivery_fee || 0;
      changes.push(`Frais de livraison: ${oldFee} → ${formData.delivery_fee} FCFA`);
    }
    if (newImage) {
      changes.push('Nouvelle image sélectionnée');
    }
    
    return changes;
  };

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center">
          <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-olive border-r-transparent"></div>
          <p className="mt-2">Chargement du produit...</p>
        </div>
      </div>
    );
  }

  if (!originalProduct) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center">
          <p className="text-red-600">Produit non trouvé</p>
          <button 
            onClick={() => navigate('/my-products')}
            className="mt-4 bg-olive text-white px-4 py-2 rounded-md hover:bg-terracotta transition"
          >
            Retour à mes produits
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-3xl font-bold text-slate">Modifier le produit</h1>
              <p className="text-gray-600 mt-1">ID: {originalProduct.id} • Créé le {new Date(originalProduct.created_at!).toLocaleDateString()}</p>
            </div>
            <button
              onClick={handleCancel}
              className="text-gray-600 hover:text-gray-800 transition"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          
          {/* Indicateur de changements */}
          {hasChanges && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
              <div className="flex items-center">
                <svg className="w-5 h-5 text-yellow-600 mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
                <div>
                  <p className="text-yellow-800 font-semibold">Modifications non sauvegardées</p>
                  <div className="text-sm text-yellow-700 mt-1">
                    {getChanges().map((change, index) => (
                      <div key={index}>• {change}</div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Colonne principale - Formulaire */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-lg shadow-md p-6">
              {/* Messages d'erreur */}
              {errors.length > 0 && (
                <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-6">
                  <h4 className="font-semibold mb-2">Erreurs à corriger :</h4>
                  <ul className="list-disc list-inside">
                    {errors.map((error, index) => (
                      <li key={index}>{error}</li>
                    ))}
                  </ul>
                </div>
              )}
              
              {/* Message de succès */}
              {success && (
                <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-6">
                  <div className="flex items-center">
                    <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    {success}
                  </div>
                  <p className="text-sm mt-1">Redirection vers la liste des produits...</p>
                </div>
              )}
              
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Nom du produit */}
                <div>
                  <label className="block text-slate font-medium mb-2" htmlFor="name">
                    Nom du produit *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name || ''}
                    onChange={handleChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-terracotta"
                  />
                </div>
                
                {/* ✅ NOUVEAU - Prix et Frais de livraison côte à côte */}
                <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                  <div>
                    <label className="block text-slate font-medium mb-2" htmlFor="price">
                      Prix unitaire (FCFA) *
                    </label>
                    <input
                      type="number"
                      id="price"
                      name="price"
                      value={formData.price || ''}
                      onChange={handleChange}
                      required
                      min="1"
                      step="1"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-terracotta"
                    />
                  </div>
                  
                  {/* ✅ NOUVEAU - Champ frais de livraison */}
                  <div>
                    <label className="block text-slate font-medium mb-2" htmlFor="delivery_fee">
                      Frais de livraison (FCFA)
                    </label>
                    <input
                      type="number"
                      id="delivery_fee"
                      name="delivery_fee"
                      value={formData.delivery_fee || ''}
                      onChange={handleChange}
                      min="0"
                      step="50"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-terracotta"
                      placeholder="500"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      0 = Livraison gratuite • Montant fixe par produit
                    </p>
                  </div>
                </div>
                
                {/* Description */}
                <div>
                  <label className="block text-slate font-medium mb-2" htmlFor="description">
                    Description du produit *
                  </label>
                  <textarea
                    id="description"
                    name="description"
                    value={formData.description || ''}
                    onChange={handleChange}
                    required
                    rows={4}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-terracotta"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Caractères: {(formData.description || '').length}
                  </p>
                </div>
                
                {/* Catégorie et Stock */}
                <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                  <div>
                    <label className="block text-slate font-medium mb-2" htmlFor="category_id">
                      Catégorie *
                    </label>
                    <select
                      id="category_id"
                      name="category_id"
                      value={formData.category_id || ''}
                      onChange={handleChange}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-terracotta"
                    >
                      {categories.map(category => (
                        <option key={category.id} value={category.id}>
                          {category.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-slate font-medium mb-2" htmlFor="stock">
                      Quantité disponible *
                    </label>
                    <input
                      type="number"
                      id="stock"
                      name="stock"
                      value={formData.stock || ''}
                      onChange={handleChange}
                      required
                      min="0"
                      step="1"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-terracotta"
                    />
                  </div>
                </div>
                
                {/* Upload nouvelle image */}
                <div>
                  <label className="block text-slate font-medium mb-2">
                    Changer l'image (optionnel)
                  </label>
                  <div className="mt-1 flex items-start space-x-4">
                    {/* Aperçu de l'image */}
                    <div className="flex-shrink-0">
                      <div className="w-32 h-32 rounded-lg overflow-hidden bg-gray-100 border-2 border-dashed border-gray-300">
                        {preview ? (
                          <img src={preview} alt="Aperçu" className="h-full w-full object-cover" />
                        ) : (
                          <div className="flex items-center justify-center h-full w-full">
                            <svg className="h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                          </div>
                        )}
                      </div>
                    </div>
                    
                    {/* Zone d'upload */}
                    <div className="flex-1">
                      <label htmlFor="file-upload" className="cursor-pointer bg-white py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 transition">
                        <span>📷 Choisir une nouvelle image</span>
                        <input
                          id="file-upload"
                          name="file-upload"
                          type="file"
                          accept="image/*"
                          className="sr-only"
                          onChange={handleImageChange}
                        />
                      </label>
                      <p className="mt-2 text-xs text-gray-500">
                        JPG, PNG, GIF ou WebP. Maximum 5MB.<br/>
                        Laissez vide pour conserver l'image actuelle.
                      </p>
                      {newImage && (
                        <p className="mt-1 text-sm text-green-600">
                          ✓ Nouvelle image: {newImage.name}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
                
                {/* ✅ NOUVEAU - Aperçu des modifications avec frais de livraison */}
                {hasChanges && formData.name && formData.price && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <h4 className="font-medium text-blue-800 mb-2">Aperçu des modifications :</h4>
                    <div className="flex items-center space-x-3">
                      {preview && (
                        <img src={preview} alt="Aperçu" className="w-16 h-16 rounded object-cover" />
                      )}
                      <div>
                        <p className="font-semibold text-blue-900">{formData.name}</p>
                        <p className="text-terracotta font-bold">{formData.price} FCFA</p>
                        <p className="text-sm text-gray-600">Stock: {formData.stock} unités</p>
                        <p className="text-sm text-blue-600">
                          Livraison: {(formData.delivery_fee || 0) > 0 ? `${formData.delivery_fee} FCFA` : 'Gratuite'}
                        </p>
                        {(formData.delivery_fee || 0) > 0 && (
                          <p className="text-sm text-green-600 font-medium">
                            Total client: {(formData.price || 0) + (formData.delivery_fee || 0)} FCFA
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Boutons d'action */}
                <div className="flex justify-between items-center pt-6 border-t">
                  <div className="flex space-x-3">
                    <button
                      type="button"
                      onClick={handleCancel}
                      className="px-6 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition"
                    >
                      Annuler
                    </button>
                    
                    {hasChanges && (
                      <button
                        type="button"
                        onClick={resetForm}
                        className="px-6 py-2 border border-yellow-300 rounded-md text-yellow-700 hover:bg-yellow-50 transition"
                      >
                        Réinitialiser
                      </button>
                    )}
                  </div>
                  
                  <button
                    type="submit"
                    disabled={saving || !hasChanges}
                    className="px-8 py-2 bg-olive text-white rounded-md hover:bg-terracotta transition duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                  >
                    {saving ? (
                      <>
                        <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Sauvegarde...
                      </>
                    ) : (
                      '💾 Sauvegarder les modifications'
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>

          {/* Colonne latérale - Informations */}
          <div className="lg:col-span-1">
            <div className="space-y-6">
              {/* Informations du produit */}
              <div className="bg-white rounded-lg shadow-md p-6">
                <h3 className="text-lg font-semibold mb-4">Informations</h3>
                <div className="space-y-3 text-sm">
                  <div>
                    <span className="text-gray-600">Créé le:</span>
                    <span className="ml-2 font-medium">
                      {new Date(originalProduct.created_at!).toLocaleDateString('fr-FR')}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-600">Dernière modification:</span>
                    <span className="ml-2 font-medium">
                      {new Date(originalProduct.updated_at!).toLocaleDateString('fr-FR')}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-600">Vendus:</span>
                    <span className="ml-2 font-medium text-terracotta">
                      {originalProduct.sold || 0} unités
                    </span>
                  </div>
                  {/* ✅ NOUVEAU - Affichage frais de livraison actuels */}
                  <div>
                    <span className="text-gray-600">Frais de livraison:</span>
                    <span className="ml-2 font-medium text-blue-600">
                      {productService.formatDeliveryFee(originalProduct.delivery_fee || 0)}
                    </span>
                  </div>
                </div>
              </div>

              {/* Actions rapides */}
              <div className="bg-white rounded-lg shadow-md p-6">
                <h3 className="text-lg font-semibold mb-4">Actions rapides</h3>
                <div className="space-y-3">
                  <button 
                    onClick={() => navigate('/my-products')}
                    className="w-full text-left px-3 py-2 text-sm text-gray-600 hover:bg-gray-50 rounded transition"
                  >
                    📋 Voir tous mes produits
                  </button>
                  <button 
                    onClick={() => navigate('/add-product')}
                    className="w-full text-left px-3 py-2 text-sm text-gray-600 hover:bg-gray-50 rounded transition"
                  >
                    ➕ Ajouter un nouveau produit
                  </button>
                  <button 
                    onClick={() => navigate('/dashboard')}
                    className="w-full text-left px-3 py-2 text-sm text-gray-600 hover:bg-gray-50 rounded transition"
                  >
                    🏠 Tableau de bord
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditProductPage;